import type { EditorProps } from "@monaco-editor/react";
import { memo, useMemo } from "react";
import type { JSX } from "react";
import { Badge } from "@/components/ui/badge";
import { ImagePreview } from "../utils/image-preview";
import { MonacoEditorPane } from "../utils/monaco-editor-pane";
import { SvgPreviewToggle } from "../utils/svg-preview-toggle";

type EditorSurfaceProps = {
  selectedFile: string | null;
  editorValue: string;
  isDirty: boolean;
  isReadOnly: boolean;
  fileKind: "text" | "svg" | "raster";
  isSvgPreviewMode: boolean;
  onToggleSvgPreview: () => void;
  onEditorChange: NonNullable<EditorProps["onChange"]>;
  editorLanguage: string;
  editorTheme: string;
  monacoOptions: NonNullable<EditorProps["options"]>;
  beforeMount: NonNullable<EditorProps["beforeMount"]>;
  onEditorDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
  onEditorDragOver?: (event: React.DragEvent<HTMLDivElement>) => void;
};

export const EditorSurface = memo(function EditorSurface({
  selectedFile,
  editorValue,
  isDirty,
  isReadOnly,
  fileKind,
  isSvgPreviewMode,
  onToggleSvgPreview,
  onEditorChange,
  editorLanguage,
  editorTheme,
  monacoOptions,
  beforeMount,
  onEditorDrop,
  onEditorDragOver,
}: EditorSurfaceProps) {
  const combinedOptions = useMemo(
    () => ({ ...monacoOptions, readOnly: isReadOnly }),
    [monacoOptions, isReadOnly],
  );

  const commonEditorProps = useMemo(
    () =>
      ({
        beforeMount,
        language: editorLanguage,
        theme: editorTheme,
        value: editorValue,
        onChange: onEditorChange,
        options: combinedOptions,
      }) satisfies Omit<Parameters<typeof MonacoEditorPane>[0], "path">,
    [
      beforeMount,
      editorLanguage,
      editorTheme,
      editorValue,
      onEditorChange,
      combinedOptions,
    ],
  );

  return (
    <div className="flex-1 flex flex-col min-w-0 min-h-0">
      <div className="h-10 border-b border-border flex items-center px-4 text-sm font-medium bg-background">
        {selectedFile ? (
          <div className="flex w-full items-center gap-3">
            <div className="flex flex-1 items-center gap-2 truncate">
              <span className="truncate">{selectedFile}</span>
              {isDirty ? (
                <Badge
                  variant="secondary"
                  size="compact"
                  className="uppercase tracking-[0.12em]"
                  aria-live="polite"
                >
                  Unsaved
                </Badge>
              ) : null}
              {isReadOnly ? (
                <Badge
                  variant="outline"
                  size="compact"
                  className="uppercase tracking-[0.12em]"
                  aria-live="polite"
                >
                  Read-only
                </Badge>
              ) : null}
            </div>
            {fileKind === "svg" ? (
              <SvgPreviewToggle
                isPreviewMode={isSvgPreviewMode}
                onToggle={onToggleSvgPreview}
                aria-label="Toggle SVG preview"
                aria-pressed={isSvgPreviewMode}
              />
            ) : null}
          </div>
        ) : (
          "Select a file"
        )}
      </div>
      <div
        className="flex-1 min-h-0"
        onDrop={onEditorDrop}
        onDragOver={onEditorDragOver}
      >
        {selectedFile ? (
          fileKind === "raster" ? (
            <ImagePreview path={selectedFile} content={editorValue} />
          ) : fileKind === "svg" ? (
            isSvgPreviewMode ? (
              <ImagePreview
                path={selectedFile}
                content={editorValue}
                className="h-full"
              />
            ) : (
              <MonacoEditorPane {...commonEditorProps} path={selectedFile} />
            )
          ) : (
            <MonacoEditorPane {...commonEditorProps} path={selectedFile} />
          )
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
            Select a file to preview its contents.
          </div>
        )}
      </div>
    </div>
  );
});
